import React from 'react';
import { object, func } from 'prop-types';
import { Global, css } from '@emotion/core';

GlobalStyles.propTypes = {
	color: object,
	font: object,
	getFocusStyles: func
};

export default function GlobalStyles({ color, font, getFocusStyles }) {
	return (
		<Global
			styles={css`
				${/*********** NORMALIZATION ************/ ''};

				html {
					line-height: 1.15;
					text-size-adjust: 100%;
					${/* Baseline at 10px means we can easily calculate REM values e.g. 1.6rem * 10px = 16px. */ ''};
					font-size: 10px;
					background-color: ${color.backgroundApp};
				}
				body,
				.p {
					font-weight: ${font.weight.regular};
					font-size: 1.4rem;
					line-height: 2rem;
				}
				body {
					margin: 0;
					color: ${color.text};
					font-family: ${font.family};
				}
				hr {
					margin-block-start: 0;
					margin-block-end: 0;
					margin-inline-start: 0;
					border: 1px solid ${color.faint};
				}
				* {
					box-sizing: border-box;
				}
				h1,
				.h1 {
					margin: 0;
					${font.h1}
				}
				h2,
				.h2 {
					margin: 0;
					${font.h2}
				}
				h3,
				.h3 {
					margin: 0;
					${font.h3}
				}
				h4,
				.h4 {
					margin: 0;
					${font.h4}
				}
				p,
				.p {
					margin-top: 0;
					margin-bottom: 1.6rem;
					hyphens: none;
					font-size: 1.4rem;
					line-height: 2rem;
				}
				.caption {
					font-size: 12px;
					line-height: 1.7rem;
				}
				a {
					color: ${color.primary};
					text-decoration: none;
					font-weight: ${font.weight.regular};
					cursor: pointer;

					&:hover {
						font-weight: ${font.weight.bold};
					}
				}
				ul,
				ol {
					margin-top: 0;
					margin-bottom: 0;
					padding-left: 0;
				}
				li {
					display: block;
				}
				b {
					font-weight: ${font.weight.bold};
				}
				button,
				input,
				select,
				textarea {
					font-family: inherit;
					font-size: 100%;
					line-height: 1.15;
					margin: 0;
				}
				button,
				input {
					overflow: visible;
					&::-ms-clear,
					&::-ms-reveal {
						display: none;
					}
				}
				button,
				select {
					text-transform: none;
					cursor: pointer;
				}
				button,
				[type='button'],
				[type='reset'],
				[type='submit'] {
					appearance: button;
				}
				button::-moz-focus-inner,
				[type='button']::-moz-focus-inner,
				[type='reset']::-moz-focus-inner,
				[type='submit']::-moz-focus-inner {
					border-style: none;
					padding: 0;
				}
				button:-moz-focusring,
				[type='button']:-moz-focusring,
				[type='reset']:-moz-focusring,
				[type='submit']:-moz-focusring {
					outline: 1px dotted ButtonText;
				}
				img,
				svg,
				.svg {
					${/* Ensure we don't render white space below the image */ ''};
					display: block;
					height: auto;
				}
				button:disabled,
				input:disabled,
				textarea:disabled,
				select:disabled,
				optgroup:disabled,
				option:disabled,
				fieldset:disabled {
					cursor: not-allowed;
				}

				${/*********** FOCUS MANAGEMENT ************/ ''};
				${/* @see https://github.com/WICG/focus-visible */ ''};

				.js-focus-visible {
					${/* Hide focus ring for mouse/touch users, show for keyboard users */ ''};
					*:focus:not(.focus-visible) {
						outline: none;
					}
					${/* Keyboard only focus styles */ ''};
					.focus-visible {
						outline: none;
						${getFocusStyles()};
					}
				}

				${/*********** COMPOSITIONAL CLASSES ************/ ''};

				@keyframes ellipsis {
					to {
						width: 1.25em;
					}
				}
				.animated-ellipsis {
					:after {
						overflow: hidden;
						display: inline-block;
						vertical-align: bottom;
						animation: ellipsis steps(4, end) 900ms infinite;
						${/* ascii code for the ellipsis character */ ''};
						content: '\\2026';
						width: 0;
					}
				}
				.truncate {
					white-space: nowrap;
					text-overflow: ellipsis;
					overflow: hidden;
				}
				.hyphenate {
					overflow-wrap: break-word;
					word-wrap: break-word;
					hyphens: auto;
					white-space: pre-wrap;
					overflow: hidden;
				}
				.uppercase {
					white-space: pre;
					text-transform: uppercase;
				}
				.bold {
					font-weight: ${font.weight.bold};
				}
				.hidden {
					display: none !important;
				}
				.invisible {
					visibility: hidden !important;
				}
				.no-margin {
					margin: 0;
				}
				.rotate-90 {
					transform: rotate(90deg);
				}
				.rotate-180 {
					transform: rotate(180deg);
				}
				.rotate-270 {
					transform: rotate(270deg);
				}
				.sr-only {
					position: absolute;
					width: 1px;
					height: 1px;
					overflow: hidden;
					clip: rect(0, 0, 0, 0);
					white-space: nowrap;
				}
				.sr-only-focusable:active,
				.sr-only-focusable:focus {
					position: static;
					width: auto;
					height: auto;
					overflow: visible;
					clip: auto;
					white-space: inherit;
				}
				.fill-parent {
					position: absolute;
					left: 0;
					top: 0;
					width: 100%;
					height: 100%;
				}
				.full-width {
					width: 100%;
				}
				.button-clear {
					appearance: none;
					border: none;
					background: none;
					padding: 0;
				}
			`}
		/>
	);
}
